import { Check } from "lucide-react";
import { useI18n } from "../lib/i18n";

type Role = "buyer" | "provider";
type Step = { buyer: string; provider: string; buyerDetail: string; providerDetail: string; partial?: boolean };
type JourneyContract = {
  negotiationId?: string;
  agreement?: { contractId: string | null };
  sourceType: string;
  title: string;
  role: Role;
  status: "pending_funding" | "funded" | "disputed" | "completed" | "cancelled";
  offerStatus: "pending" | "accepted" | "declined";
  providerSignedAt?: string | null;
  buyerSignedAt?: string | null;
  milestones: { status: "pending" | "released" | "cancelled" }[];
};

const stage = (buyer: string, provider: string, buyerDetail: string, providerDetail: string): Step => ({ buyer, provider, buyerDetail, providerDetail });

// The progress card serves contracts from several flows. Their stored status
// alone cannot show the next action: projects, services and business sales wait for signatures,
// while hires and RFQs wait on an offer and orders settle through payment.
function journey(contract: JourneyContract): { title: string; steps: Step[]; current: number } {
  const releaseCount = contract.milestones.filter((milestone) => milestone.status === "released").length;
  const releaseDetail = `${releaseCount}/${contract.milestones.length} released.`;
  const work = stage("Review & release", "Complete work", `Release when satisfied. ${releaseDetail}`, `Deliver the work. ${releaseDetail}`);
  const complete = stage("Complete", "Complete", "All milestones released.", "All milestones released.");
  const signatureCount = contract.negotiationId ? 0 : Number(!!contract.providerSignedAt) + Number(!!contract.buyerSignedAt);
  const signatureDetail = `${signatureCount}/2 signatures. ${contract.offerStatus !== "accepted" ? "Sign after the proposal is accepted." : signatureCount === 2 ? "Both parties signed." : "Either party can sign first."}`;
  // [contracts.signature-eligibility](../../../docs/product/contracts.md#contracts-signature-eligibility): projects, services and business sales share independent
  // consent. One signature completes half the stage; negotiation excludes old consent.
  const signatureStep = { ...stage("Sign agreement", "Sign agreement", signatureDetail, signatureDetail),
    partial: !contract.negotiationId && contract.offerStatus === "accepted" && signatureCount === 1 };

  let title: string;
  let steps: Step[];
  let current: number;
  switch (contract.sourceType) {
    case "job":
      title = "Job contract process";
      steps = [stage("Hire confirmed", "Hire confirmed", "Monthly salary accepted.", "Monthly salary accepted."), signatureStep,
        stage("Fund full contract", "Escrow funding", "Fund all monthly milestones upfront.", "Await full contract funding."),
        stage("Release monthly payment", "Record work", `Manual releases only. ${releaseDetail}`, `Work updates do not release payment. ${releaseDetail}`), complete];
      current = signatureCount < 2 ? 1 : contract.status === "pending_funding" ? 2 : 3;
      break;
    case "project":
      title = "Project contract process";
      steps = [
        stage("Bid awarded", "Bid awarded", "Provider selected.", "Your bid was selected."),
        signatureStep,
        stage("Fund escrow", "Escrow funding", "Fund before work starts.", "Await funding."),
        work, complete,
      ];
      if (contract.negotiationId) {
        // [contracts.current-proposal](../../../docs/product/contracts.md#contracts-current-proposal): awarded engagements negotiate inside the contract journey;
        // legacy proposals without a contract retain their pre-award path.
        const negotiation = stage("Negotiation", "Negotiation", "Discuss the proposal in Messages.", "Discuss the proposal in Messages.");
        const alreadyAwarded = !!contract.agreement?.contractId;
        steps = alreadyAwarded ? [steps[0], negotiation, ...steps.slice(1)] : [negotiation, ...steps];
        current = alreadyAwarded ? 1 : 0;
      } else current = contract.offerStatus === "pending" || signatureCount < 2 ? 1 : contract.status === "pending_funding" ? 2 : 3;
      break;
    case "service":
      title = "Service contract process";
      steps = [
        stage("Quote accepted", "Quote accepted", "You approved the quote.", "Client approved your quote."),
        signatureStep,
        stage("Fund escrow", "Escrow funding", "Fund to start work.", "Await funding."),
        work, complete,
      ];
      current = contract.offerStatus !== "accepted" || signatureCount < 2 ? 1 : contract.status === "pending_funding" ? 2 : 3;
      break;
    case "business":
      // [items.business-sale](../../../docs/product/items.md#items-business-sale): both signatures and payment gate the agreed
      // transfer milestones, and their release does not itself transfer legal ownership.
      title = "Business sale process";
      steps = [
        stage("Quotation accepted", "Quotation accepted", "You approved the sale terms.", "Buyer approved the sale terms."),
        signatureStep,
        stage("Fund escrow", "Escrow funding", "Fund the agreed sale total.", "Await buyer funding."),
        stage("Review & release", "Complete transfer milestones", `Review each agreed transfer milestone before release. ${releaseDetail}`, `Complete the agreed transfer milestones. ${releaseDetail}`),
        complete,
      ];
      current = contract.offerStatus !== "accepted" || signatureCount < 2 ? 1 : contract.status === "pending_funding" ? 2 : 3;
      break;
    case "rfq_items":
      title = "Items RFQ agreement process";
      steps = [
        stage("Items accepted", "Items accepted", "Your selected quantities are allocated.", "Buyer approved these supplied items."),
        stage("Seller signs", "Sign agreement", "Await seller's signature.", "Sign the accepted supply terms."),
        stage("Sign agreement", "Buyer signs", "Sign after the seller.", "Await buyer's signature."),
        stage("Fund escrow", "Escrow funding", "Fund this agreement's accepted items.", "Await funding before supply."),
        stage("Confirm receipt & release", "Supply agreed items", "Confirm all goods in this agreement before full release.", "Supply all agreed goods for one receipt confirmation."),
        stage("Complete", "Complete", "Agreement receipt confirmed.", "Agreement payment released."),
      ];
      current = !contract.providerSignedAt ? 1 : !contract.buyerSignedAt ? 2 : contract.status === "pending_funding" ? 3 : 4;
      break;
    case "rfq":
      title = "RFQ contract process";
      steps = [
        stage("Quote awarded", "Quote awarded", "Supplier selected.", "Your quote was selected."),
        stage("Supplier accepts", "Accept offer", "Await supplier's decision.", "Accept or decline."),
        stage("Fund escrow", "Escrow funding", "Fund before work starts.", "Await funding."),
        work, complete,
      ];
      current = contract.offerStatus === "pending" ? 1 : contract.status === "pending_funding" ? 2 : 3;
      break;
    case "order":
      title = "Order contract process";
      steps = [
        stage("Order paid", "Buyer paid", "Payment captured.", "Buyer payment captured."),
        stage("Confirm receipt", "Fulfill order", "Confirm when received.", "Ship or deliver the item."),
        stage("Order complete", "Order complete", "Receipt confirmed.", "Buyer confirmed receipt."),
      ];
      current = 1;
      break;
    case "hire":
      title = contract.title.startsWith("Service:") ? "Fixed-price service booking process" : "Direct hire process";
      steps = [
        stage("Offer sent", "Offer received", "Provider notified.", "Client sent an offer."),
        stage("Provider accepts", "Accept offer", "Await provider's decision.", "Accept or decline."),
        stage("Fund escrow", "Escrow funding", "Fund after acceptance.", "Await funding."),
        work, complete,
      ];
      current = contract.offerStatus === "pending" ? 1 : contract.status === "pending_funding" ? 2 : 3;
      break;
    default:
      title = "Contract progress";
      steps = [
        stage("Agreement created", "Agreement created", "Contract ready.", "Contract ready."),
        stage("Fund escrow", "Escrow funded", "Fund the agreement.", "Await funding."),
        work, complete,
      ];
      current = contract.status === "pending_funding" ? 1 : 2;
  }

  if (contract.status === "completed") current = steps.length;
  if (contract.status === "cancelled" || contract.offerStatus === "declined") current = -1;
  return { title, steps, current };
}

export function ContractJourney({ contract, mobile = false }: { contract: JourneyContract; mobile?: boolean }) {
  const { title, steps, current } = journey(contract);
  if (mobile) return <MobileContractJourney contract={contract} steps={steps} current={current} />;
  // [contracts.lists-and-process](../../../docs/product/contracts.md#contracts-lists-and-process): cancellation is already named in Contract Summary; keep the ended
  // explanation here without repeating its badge or implying later steps apply.
  return <JourneyProgress title={title} steps={steps.map((step) => ({ label: step[contract.role], detail: contract.role === "buyer" ? step.buyerDetail : step.providerDetail, partial: step.partial }))} current={current}
    paused={contract.status === "disputed"}
    outcome={current === -1 ? contract.offerStatus === "declined" ? "Offer declined" : undefined : current === steps.length ? "Complete" : undefined}
    notice={current === -1 ? "This contract has ended. The remaining steps no longer apply." : undefined} />;
}

// Work and purchase journeys share presentation, while their callers own stage rules.
// current is zero-based; steps.length marks completion and -1 stops the path.
// paused suppresses the current-action marker while retaining completed steps.
// Optional partial marks half-completed current work without advancing the path.
export function JourneyProgress({ title, steps, current, outcome, notice, paused = false }: {
  title: string;
  steps: ReadonlyArray<{ label: string; detail: string; partial?: boolean }>;
  current: number;
  outcome?: string;
  notice?: string;
  paused?: boolean;
}) {
  const stopped = current === -1;
  return <section className="card mt-5 p-5 sm:p-7" aria-label={title}>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h2 className="text-lg font-bold text-navy">{title}</h2>
      {outcome && <span className={`chip ${current === steps.length ? "bg-brand-green/10 text-brand-greendark" : "bg-navy-50 text-mut"}`}>{outcome}</span>}
    </div>
    {notice && <p className="mt-1 text-xs text-mut">{notice}</p>}
    {steps.length > 3 && <p className="mt-2 text-xs text-mut sm:hidden">Swipe sideways to see every step.</p>}
    <div className="mt-5 overflow-x-auto pb-2">
      <ol className={`flex ${steps.length <= 3 ? "w-full" : steps.length > 5 ? "min-w-[840px]" : "min-w-[660px]"}`}>
        {steps.map((step, index) => {
          const done = !stopped && index < current;
          const active = !stopped && !paused && index === current;
          const partial = active && step.partial;
          return <li key={index} className="relative flex-1 min-w-0 px-2 text-center" aria-current={active ? "step" : undefined}>
            {index < steps.length - 1 && <span aria-hidden="true" className={`absolute left-[calc(50%+26px)] top-5 h-px w-[calc(100%-52px)] ${done ? "bg-brand-green" : "bg-line"}`} />}
            <span className={`relative z-10 mx-auto flex h-10 w-10 items-center justify-center rounded-full text-base font-bold ${done ? "bg-brand-green text-white" : partial ? "overflow-hidden bg-blue-50 text-navy ring-2 ring-brand-green/30" : active ? "border-2 border-blue-600 bg-white text-blue-600" : "bg-blue-50 text-blue-600"}`}>
              {partial && <span className="absolute inset-y-0 left-0 w-1/2 bg-brand-green" aria-hidden="true" />}
              {done ? <Check className="h-5 w-5" aria-hidden="true" /> : <span className="relative">{index + 1}</span>}
            </span>
            <span className={`mt-3 block text-xs font-semibold ${active || done ? "text-navy" : "text-navy-600"}`}>{step.label}</span>
            <span className="mt-1 block text-xs leading-relaxed text-mut">{step.detail}</span>
          </li>;
        })}
      </ol>
    </div>
  </section>;
}

const MOBILE_STEP_LABELS: Readonly<Record<string, string>> = {
  "Bid awarded": "awarded", "Quote accepted": "quoteAccepted", "Quote awarded": "quoteAwarded",
  "Contractor signs": "contractorSigns", "Provider signs": "providerSigns", "Accept & sign": "acceptSign",
  "Sign agreement": "signAgreement", "Client signs": "clientSigns", "Fund escrow": "fundEscrow",
  "Escrow funding": "escrowFunding", "Escrow funded": "escrowFunded", "Review & release": "reviewRelease",
  "Complete work": "completeWork", "Complete": "complete", "Supplier accepts": "supplierAccepts",
  "Accept offer": "acceptOffer", "Order paid": "orderPaid", "Buyer paid": "buyerPaid",
  "Confirm receipt": "confirmReceipt", "Fulfill order": "fulfillOrder", "Order complete": "orderComplete",
  "Offer sent": "offerSent", "Offer received": "offerReceived", "Provider accepts": "providerAccepts",
  "Agreement created": "agreementCreated",
};

// The mobile list uses the same role/source-specific process and current-step calculation.
function MobileContractJourney({ contract, steps, current }: { contract: JourneyContract; steps: Step[]; current: number }) {
  const { t } = useI18n();
  const stopped = current === -1;
  const paused = contract.status === "disputed";
  return <section className="mobile-contract-detail-section" aria-label={t("contracts.detail.process")}>
    <h2>{t("contracts.detail.process")}</h2>
    {stopped && <p className="mb-3 text-mut">{t("contracts.detail.processEnded")}</p>}
    <ol className="mobile-contract-journey">
      {steps.map((step, index) => {
        const done = !stopped && index < current;
        const active = !stopped && !paused && index === current;
        const partial = active && step.partial;
        const label = step[contract.role];
        return <li key={index} aria-current={active ? "step" : undefined}>
          <span className={`mobile-contract-step ${done ? "done" : partial ? "partial" : active ? "active" : ""}`} aria-hidden="true">{done ? <Check /> : index + 1}</span>
          <div><strong>{MOBILE_STEP_LABELS[label] ? t(`contracts.detail.steps.${MOBILE_STEP_LABELS[label]}`) : label}</strong>
            <p>{contract.role === "buyer" ? step.buyerDetail : step.providerDetail}</p></div>
        </li>;
      })}
    </ol>
  </section>;
}
